/**
 * Category Cards Carousel
 * A custom element for horizontal card carousel with:
 * - Arrow button navigation
 * - CSS scroll-snap for smooth native scrolling
 * - Touch/swipe support (native via CSS)
 * - Keyboard navigation (arrow keys)
 * - IntersectionObserver for lazy loading animations
 * - ResizeObserver for responsive recalculation
 * - Auto-scroll functionality with pause on hover
 */

if (!customElements.get('category-cards-carousel')) {
  class CategoryCardsCarousel extends HTMLElement {
    constructor() {
      super();

      this.slider = this.querySelector('[id^="CategoryCardsSlider-"]');
      this.sliderItems = this.querySelectorAll('.category-card');
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');

      if (!this.slider || !this.nextButton) return;

      // Auto-scroll settings
      this.autoScrollEnabled = this.dataset.autoScroll === 'true';
      this.autoScrollSpeed = parseInt(this.dataset.autoScrollSpeed, 10) || 4;
      this.autoScrollInterval = null;
      this.isPaused = false;

      this.initCarousel();
      this.initLazyLoading();
      this.initKeyboardNavigation();

      if (this.autoScrollEnabled) {
        this.initAutoScroll();
      }
    }

    initCarousel() {
      // Get visible slider items
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(
        (element) => element.clientWidth > 0
      );

      if (this.sliderItemsToShow.length < 2) return;

      // Calculate slide offset (width + gap)
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

      // Bind events
      this.slider.addEventListener('scroll', this.handleScroll.bind(this));
      this.prevButton.addEventListener('click', this.handlePrevClick.bind(this));
      this.nextButton.addEventListener('click', this.handleNextClick.bind(this));

      // Handle resize for responsive recalculation
      const resizeObserver = new ResizeObserver(() => {
        this.recalculateOffsets();
        this.updateButtonStates();
      });
      resizeObserver.observe(this.slider);

      // Initial button state
      this.updateButtonStates();
    }

    recalculateOffsets() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(
        (element) => element.clientWidth > 0
      );

      if (this.sliderItemsToShow.length >= 2) {
        this.sliderItemOffset =
          this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      }
    }

    handleScroll() {
      // Debounce scroll handling for performance
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      this.scrollTimeout = setTimeout(() => {
        this.updateButtonStates();
      }, 50);
    }

    handlePrevClick(event) {
      event.preventDefault();
      this.resetAutoScrollTimer();
      const newPosition = this.slider.scrollLeft - this.sliderItemOffset;
      this.scrollToPosition(Math.max(0, newPosition));
    }

    handleNextClick(event) {
      event.preventDefault();
      this.resetAutoScrollTimer();
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      const newPosition = this.slider.scrollLeft + this.sliderItemOffset;
      this.scrollToPosition(Math.min(maxScroll, newPosition));
    }

    scrollToPosition(position) {
      this.slider.scrollTo({
        left: position,
        behavior: 'smooth'
      });
    }

    updateButtonStates() {
      const scrollLeft = this.slider.scrollLeft;
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;

      // Disable prev button at start
      if (scrollLeft <= 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      // Disable next button at end (with small tolerance for rounding errors)
      if (scrollLeft >= maxScroll - 5) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }

    initLazyLoading() {
      // Use IntersectionObserver to add 'is-visible' class for entrance animations
      const observerOptions = {
        root: null, // viewport
        rootMargin: '50px',
        threshold: 0.1
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            // Once visible, no need to keep observing
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      this.sliderItems.forEach((item) => observer.observe(item));
    }

    initKeyboardNavigation() {
      // Allow keyboard navigation when focused within carousel
      this.addEventListener('keydown', (event) => {
        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.handlePrevClick(event);
          this.prevButton.focus();
        } else if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.handleNextClick(event);
          this.nextButton.focus();
        }
      });
    }

    // Auto-scroll functionality
    initAutoScroll() {
      // Start auto-scroll
      this.startAutoScroll();

      // Pause on hover
      this.addEventListener('mouseenter', () => {
        this.pauseAutoScroll();
      });

      this.addEventListener('mouseleave', () => {
        this.resumeAutoScroll();
      });

      // Pause on focus (for accessibility)
      this.addEventListener('focusin', () => {
        this.pauseAutoScroll();
      });

      this.addEventListener('focusout', (event) => {
        // Only resume if focus left the carousel entirely
        if (!this.contains(event.relatedTarget)) {
          this.resumeAutoScroll();
        }
      });

      // Pause when not visible (for performance)
      const visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resumeAutoScroll();
          } else {
            this.pauseAutoScroll();
          }
        });
      }, { threshold: 0.1 });

      visibilityObserver.observe(this);
    }

    startAutoScroll() {
      if (this.autoScrollInterval) return;

      this.autoScrollInterval = setInterval(() => {
        if (!this.isPaused) {
          this.autoScrollNext();
        }
      }, this.autoScrollSpeed * 1000);
    }

    stopAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
      }
    }

    pauseAutoScroll() {
      this.isPaused = true;
    }

    resumeAutoScroll() {
      this.isPaused = false;
    }

    resetAutoScrollTimer() {
      if (this.autoScrollEnabled) {
        this.stopAutoScroll();
        this.startAutoScroll();
      }
    }

    autoScrollNext() {
      const scrollLeft = this.slider.scrollLeft;
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;

      // If at the end, scroll back to start
      if (scrollLeft >= maxScroll - 5) {
        this.scrollToPosition(0);
      } else {
        // Otherwise, scroll to next card
        const newPosition = scrollLeft + this.sliderItemOffset;
        this.scrollToPosition(Math.min(maxScroll, newPosition));
      }
    }

    // Cleanup when element is removed from DOM
    disconnectedCallback() {
      this.stopAutoScroll();
    }
  }

  customElements.define('category-cards-carousel', CategoryCardsCarousel);
}
